<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MiraSupportAPI from 'dashboard/api/miraSupport';

// What Mira may learn waits here. Mira proposes items from resolved
// conversations and help articles, and agents propose from a conversation;
// Mira uses an item only after an administrator approves it, and never one
// approved by the person who proposed it.
const { t } = useI18n();

const STATUSES = ['proposed', 'approved', 'rejected', 'retired'];
const WARNINGS = [
  'personal_details_removed',
  'contains_amount_check_before_approving',
];

const status = ref('proposed');
const items = ref([]);
const edits = ref({});
const loading = ref(true);
const busy = ref('');

const errorMessage = error => {
  const code = error?.response?.data?.error;
  return ['own_proposal', 'already_decided', 'knowledge_not_found'].includes(
    code
  )
    ? t(`MIRA_KNOWLEDGE_MGMT.ERRORS.${code.toUpperCase()}`)
    : t('MIRA_KNOWLEDGE_MGMT.ERRORS.UNAVAILABLE');
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await MiraSupportAPI.knowledge(status.value);
    items.value = data.items || [];
    edits.value = Object.fromEntries(
      items.value.map(item => [
        item.id,
        { title: item.title, question: item.question, answer: item.answer },
      ])
    );
  } catch (error) {
    items.value = [];
    useAlert(errorMessage(error));
  } finally {
    loading.value = false;
  }
};

const show = next => {
  status.value = next;
  load();
};

const decide = async (item, decision) => {
  if (busy.value) return;
  busy.value = `${decision}-${item.id}`;
  try {
    const edited = edits.value[item.id];
    const changes =
      decision === 'approve'
        ? Object.fromEntries(
            ['title', 'question', 'answer']
              .filter(field => edited[field].trim() !== item[field])
              .map(field => [field, edited[field].trim()])
          )
        : {};
    await MiraSupportAPI.decideKnowledge(item.id, { decision, ...changes });
    useAlert(t(`MIRA_KNOWLEDGE_MGMT.DONE.${decision.toUpperCase()}`));
    await load();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    busy.value = '';
  }
};

const proposeArticles = async () => {
  try {
    await MiraSupportAPI.proposeArticles();
    useAlert(t('MIRA_KNOWLEDGE_MGMT.ARTICLES_QUEUED'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

onMounted(load);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('MIRA_KNOWLEDGE_MGMT.LOADING')"
    :no-records-found="!items.length"
    :no-records-message="
      $t(`MIRA_KNOWLEDGE_MGMT.EMPTY.${status.toUpperCase()}`)
    "
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('MIRA_KNOWLEDGE_MGMT.HEADER')"
        :description="$t('MIRA_KNOWLEDGE_MGMT.DESCRIPTION')"
        feature-name="mira_knowledge"
      >
        <template #actions>
          <Button
            size="sm"
            variant="faded"
            icon="i-lucide-book-open"
            :label="$t('MIRA_KNOWLEDGE_MGMT.PROPOSE_ARTICLES')"
            @click="proposeArticles"
          />
        </template>
      </BaseSettingsHeader>
      <div class="flex gap-2 pt-4" role="tablist">
        <Button
          v-for="option in STATUSES"
          :key="option"
          size="sm"
          role="tab"
          :data-test-id="`mira-knowledge-tab-${option}`"
          :aria-selected="status === option"
          :variant="status === option ? 'solid' : 'ghost'"
          :color="status === option ? 'blue' : 'slate'"
          :label="$t(`MIRA_KNOWLEDGE_MGMT.STATUS.${option.toUpperCase()}`)"
          @click="show(option)"
        />
      </div>
    </template>
    <template #body>
      <ul class="flex flex-col gap-4 p-0">
        <li
          v-for="item in items"
          :key="item.id"
          data-test-id="mira-knowledge-item"
          class="flex list-none flex-col gap-2 rounded-xl border border-n-weak p-4"
        >
          <p class="mb-0 text-xs text-n-slate-11">
            {{
              $t(
                `MIRA_KNOWLEDGE_MGMT.SOURCE.${item.sourceKind.toUpperCase()}`,
                {
                  by:
                    item.proposedBy === 'relay:auto'
                      ? $t('MIRA_KNOWLEDGE_MGMT.MIRA')
                      : item.proposedBy,
                }
              )
            }}
          </p>
          <template v-if="status === 'proposed'">
            <label class="text-xs font-medium text-n-slate-12">
              {{ $t('MIRA_KNOWLEDGE_MGMT.FIELDS.TITLE') }}
              <input
                v-model="edits[item.id].title"
                maxlength="200"
                class="mt-1 w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-1.5 text-sm"
              />
            </label>
            <label class="text-xs font-medium text-n-slate-12">
              {{ $t('MIRA_KNOWLEDGE_MGMT.FIELDS.QUESTION') }}
              <textarea
                v-model="edits[item.id].question"
                rows="2"
                maxlength="1000"
                class="mt-1 w-full rounded-lg border border-n-weak bg-n-surface-1 p-2 text-sm"
              />
            </label>
            <label class="text-xs font-medium text-n-slate-12">
              {{ $t('MIRA_KNOWLEDGE_MGMT.FIELDS.ANSWER') }}
              <textarea
                v-model="edits[item.id].answer"
                rows="4"
                maxlength="4000"
                class="mt-1 w-full rounded-lg border border-n-weak bg-n-surface-1 p-2 text-sm"
              />
            </label>
          </template>
          <template v-else>
            <h3 class="mb-0 text-sm font-medium text-n-slate-12">
              {{ item.title }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">{{ item.question }}</p>
            <p class="mb-0 whitespace-pre-wrap text-sm text-n-slate-12">
              {{ item.answer }}
            </p>
            <p v-if="item.decidedBy" class="mb-0 text-xs text-n-slate-11">
              {{ $t('MIRA_KNOWLEDGE_MGMT.DECIDED_BY', { by: item.decidedBy }) }}
            </p>
          </template>
          <p
            v-for="warning in item.warnings.filter(w => WARNINGS.includes(w))"
            :key="warning"
            class="mb-0 text-xs text-n-amber-11"
          >
            {{ $t(`MIRA_KNOWLEDGE_MGMT.WARNINGS.${warning}`) }}
          </p>
          <div v-if="status === 'proposed'" class="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              color="ruby"
              :data-test-id="`mira-knowledge-reject-${item.id}`"
              :label="$t('MIRA_KNOWLEDGE_MGMT.REJECT')"
              :is-loading="busy === `reject-${item.id}`"
              @click="decide(item, 'reject')"
            />
            <Button
              size="sm"
              :data-test-id="`mira-knowledge-approve-${item.id}`"
              :label="$t('MIRA_KNOWLEDGE_MGMT.APPROVE')"
              :is-loading="busy === `approve-${item.id}`"
              @click="decide(item, 'approve')"
            />
          </div>
          <div v-else-if="status === 'approved'" class="flex justify-end">
            <Button
              size="sm"
              variant="ghost"
              color="slate"
              :label="$t('MIRA_KNOWLEDGE_MGMT.RETIRE')"
              :is-loading="busy === `retire-${item.id}`"
              @click="decide(item, 'retire')"
            />
          </div>
        </li>
      </ul>
    </template>
  </SettingsLayout>
</template>
